import { EVENT_KEYS } from 'presentation/events';
import { createRequesterMediator } from '../utils';
import { saveEntry } from 'core/services/tasks.service';

const generateId = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

const toTaskLike = (entry) => ({
    id: entry.id,
    title: entry.title,
    dueDateTime: null,
    reminderDateTime: null,
    isReminderOn: false,
    status: entry.complete ? 'completed' : 'notStarted',
    importance: entry.priority ? 'high' : 'normal',
    linkedResources: [{ displayName: `${entry.type}|${entry.amount}|ignore:${entry.ignore}` }]
});

const saveEntries = () => {
    return (dispatch, getState) => {
        let { entries, login, selectedList } = getState();
        saveEntry(login.user.id, selectedList.id, entries);
    };
};

const addEntry = (addedEntry) => {
    return async (dispatch, getState) => {
        let { login, selectedList, entries } = getState();

        dispatch({
            type : EVENT_KEYS.SHOW_ADD_ENTRY,
            payload : false
        });

        const newEntry = {
            id: generateId(),
            title: addedEntry.title,
            order: addedEntry.insertOrder !== undefined ? addedEntry.insertOrder : entries.length,
            payments: [],
            ignore: false,
            parent: addedEntry.parent,
            type: addedEntry.type,
            tag: addedEntry.tag,
            category: addedEntry.category,
            amount: addedEntry.amount,
            complete: false,
            priority: addedEntry.priority || false,
            date: addedEntry.date || addedEntry.dueDate || null,
            reminderDate: addedEntry.reminderDate || null,
            isReminderOn: addedEntry.isReminderOn || false,
        };

        let storePayload = {
            type : addedEntry.insertOrder ? EVENT_KEYS.INSERT_ENTRY : EVENT_KEYS.ADD_ENTRY,
            payload : {
                task : toTaskLike(newEntry),
                entry : newEntry
            }
        };
        dispatch(storePayload);

        entries = getState().entries;
        let parentEntry = entries.find((entry) => entry.id === addedEntry.parent);
        if(parentEntry) {
            parentEntry.payments.push({
                name : newEntry.title,
                amount : addedEntry.amount,
                id : newEntry.id
            });
        }
        saveEntry(login.user.id, selectedList.id, entries);
    };
};

const updateEntry = (updateInfo) => {
    return (dispatch, getState) => {
        dispatch({
            type : EVENT_KEYS.UPDATE_ENTRY,
            payload : updateInfo
        });
        dispatch(saveEntries());
    };
};

const deleteEntry = (entryDeleted) => {
    return async (dispatch, getState) => {
        dispatch({
            type : EVENT_KEYS.REMOVE_ENTRY,
            payload : entryDeleted
        });
        dispatch(saveEntries());
    };
};

const updateAllEntries = (newEntries) => {
    return (dispatch, getState) => {
        dispatch({
            type : EVENT_KEYS.UPDATE_ALL_ENTRIES,
            payload : newEntries
        });

        dispatch(saveEntries());
    };
};

export const mediateAddEntryRequester = (addEntrySignal, dispatch) => {
    const handleSignal = (payload) => {
        dispatch(addEntry(payload));
    };
    return createRequesterMediator({ signalHandler : handleSignal })(addEntrySignal);
};

export const mediateUpdateEntryRequester = (updateEntrySignal, dispatch) => {
    const handleSignal = (payload) => {
        dispatch(updateEntry(payload));
    };
    return createRequesterMediator({ signalHandler : handleSignal })(updateEntrySignal);
};

export const mediateUpdateEntriesRequester = (updateEntrySignal, dispatch) => {
    const handleSignal = (newEntries) => {
        dispatch(updateAllEntries(newEntries));
    };
    return createRequesterMediator({ signalHandler : handleSignal })(updateEntrySignal);
};

export const mediateDeleteEntryRequester = (updateEntrySignal, dispatch) => {
    const handleSignal = (payload) => {
        dispatch(deleteEntry(payload));
    };
    return createRequesterMediator({ signalHandler : handleSignal })(updateEntrySignal);
};

export const mediateBeginAddEntryRequester = (editTaskSignal, dispatch) => {
    return ((signal) => {
        const handleSignal = (payload) => {
            dispatch({
                type : EVENT_KEYS.BEGIN_ADD_ENTRY
            });

            dispatch({
                type : EVENT_KEYS.SHOW_ADD_ENTRY,
                payload : {
                    show : true,
                    ...payload
                }
            });
        };

        signal.add(handleSignal);

        return {
            destroy() {
                signal.remove(handleSignal);
            }
        };
    })(editTaskSignal);
};

export const mediateEditEntryRequester = (editTaskSignal, dispatch) => {
    return ((signal) => {
        const handleSignal = (payload) => {
            if(payload) {
                dispatch({
                    type : EVENT_KEYS.BEGIN_EDIT_ENTRY,
                    payload
                });

                dispatch({
                    type : EVENT_KEYS.SHOW_EDIT_ENTRY,
                    payload : true
                });
            } else {
                dispatch({
                    type : EVENT_KEYS.SHOW_EDIT_ENTRY,
                    payload : false
                });

                dispatch({
                    type : EVENT_KEYS.END_EDIT_ENTRY
                });
            }
        };

        signal.add(handleSignal);

        return {
            destroy() {
                signal.remove(handleSignal);
            }
        };
    })(editTaskSignal);
};

export const mediateEditEntryPaymentsRequester = (editPaymentsTaskSignal, dispatch) => {
    return ((signal) => {
        const handleSignal = (payload) => {
            if(payload) {
                dispatch({
                    type : EVENT_KEYS.BEGIN_EDIT_ENTRY_PAYMENTS,
                    payload
                });

                dispatch({
                    type : EVENT_KEYS.SHOW_EDIT_ENTRY_PAYMENTS,
                    payload : true
                });
            } else {
                dispatch({
                    type : EVENT_KEYS.SHOW_EDIT_ENTRY_PAYMENTS,
                    payload : false
                });

                dispatch({
                    type : EVENT_KEYS.END_EDIT_ENTRY_PAYMENTS
                });
            }
        };

        signal.add(handleSignal);

        return {
            destroy() {
                signal.remove(handleSignal);
            }
        };
    })(editPaymentsTaskSignal);
};
